import axios, { type AxiosResponse } from 'axios';

import { showAiCreditsExhaustedModal } from '@/lib/ai-credits-gate';
import {
  isStaleSessionHtmlResponse,
  resolveSafeSignInTarget,
  shouldRedirectStaleSessionOnAxiosError,
  toAbsoluteUrl,
} from '@/lib/http-stale-session';
import { newUserSessionPath } from '@/lib/routes';
import { getDocument, isBrowser } from '@/lib/utils';

// Axios client configured to use Rails CSRF via cookie
// - Rails sets cookie "XSRF-TOKEN" (see ApplicationController#set_csrf_cookie)
// - Axios will automatically send the cookie "XSRF-TOKEN" as "X-CSRF-Token" header
const http = axios.create({
  xsrfHeaderName: 'X-CSRF-Token',
  xsrfCookieName: 'XSRF-TOKEN',
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
  withCredentials: true,
});

export function extractApiErrorMessage(error: unknown): string | null {
  if (!axios.isAxiosError(error)) return null;

  const data = error.response?.data;
  if (!data || typeof data !== 'object') return null;

  // Check errors field (can be string, array, or object)
  const errors = (data as Record<string, unknown>).errors;
  if (typeof errors === 'string') return errors;
  if (Array.isArray(errors) && typeof errors[0] === 'string') return errors[0];
  if (errors && typeof errors === 'object') {
    for (const value of Object.values(errors as Record<string, unknown>)) {
      if (typeof value === 'string') return value;
      if (Array.isArray(value) && typeof value[0] === 'string') return value[0];
    }
  }

  // Check error field (string or array)
  const errorField = (data as Record<string, unknown>).error;
  if (typeof errorField === 'string') return errorField;
  if (Array.isArray(errorField) && typeof errorField[0] === 'string') return errorField[0];

  // Check message field (string or array)
  const message = (data as Record<string, unknown>).message;
  if (typeof message === 'string') return message;
  if (Array.isArray(message) && typeof message[0] === 'string') return message[0];

  return null;
}

const DEFAULT_SIGN_IN_PATH = newUserSessionPath();

const redirectToSignIn = (url?: string): void => {
  if (!isBrowser()) return;

  const document = getDocument();
  if (!document) return;

  const current = toAbsoluteUrl(document.location.href);
  if (!current) return;

  const target = resolveSafeSignInTarget(url, current, DEFAULT_SIGN_IN_PATH);

  if (current.href === target.href) return;

  document.location.assign(target.href);
};

// Centralized stale-session handling for async CMS requests.
// Redirects to sign-in on auth failures instead of leaving feature-level error states.
http.interceptors.response.use(
  (response: AxiosResponse) => {
    if (
      !isBrowser() ||
      !isStaleSessionHtmlResponse(response, {
        documentHref: getDocument()?.location.href ?? '',
        defaultSignInPath: DEFAULT_SIGN_IN_PATH,
      })
    ) {
      return response;
    }

    redirectToSignIn(response.request?.responseURL);
    return Promise.reject(new axios.CanceledError('Session expired. Redirecting to sign in.'));
  },
  (error) => {
    const isTokenRequest = error.config?.url === '/api/v1/ai/tokens';
    if (
      !isTokenRequest &&
      error.response?.status === 402 &&
      error.response.data?.error === 'ai_credits_exhausted'
    ) {
      showAiCreditsExhaustedModal(error.response.data.message);
      return Promise.reject(new axios.CanceledError('AI credits exhausted'));
    }

    const currentHref = getDocument()?.location.href ?? '';
    if (
      !shouldRedirectStaleSessionOnAxiosError(error, {
        currentHref,
        defaultSignInPath: DEFAULT_SIGN_IN_PATH,
      })
    ) {
      return Promise.reject(error);
    }

    const inertiaLocation = error.response?.headers?.['x-inertia-location'] as string | undefined;
    redirectToSignIn(inertiaLocation);
    return Promise.reject(new axios.CanceledError('Session expired. Redirecting to sign in.'));
  },
);

export default http;
